import { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2, Star, GripVertical } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useCategories } from '../hooks/useCategories';
import { toast } from 'sonner';

const TIER_OPTIONS = [
  { value: 'everyone', label: 'Everyone (public)' },
  { value: 'bronze', label: 'Bronze & up' },
  { value: 'silver', label: 'Silver & up' },
  { value: 'gold', label: 'Gold & up' },
  { value: 'platinum', label: 'Platinum only' },
  { value: 'free_comp', label: 'Practitioner collective (Free-Comp & admin)' },
];
const TIER_LABEL = Object.fromEntries(TIER_OPTIONS.map((o) => [o.value, o.label]));

export const AdminForumTab = () => {
  const {
    categories,
    editingCategory,
    categoryForm,
    setCategoryForm,
    fetchCategories,
    resetCategoryForm,
    handleEditCategory,
    saveCategory,
    deleteCategory
  } = useCategories();

  const categoryFormRef = useRef(null);
  const [practitioners, setPractitioners] = useState([]);
  // Dragged order is held locally so the list moves under the cursor, then saved.
  const [order, setOrder] = useState(null);
  const [dragId, setDragId] = useState(null);
  // The id also lives in a ref: a dragover can arrive in the same tick as the dragstart, and a
  // state read inside that handler would still be null.
  const dragRef = useRef(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const ordered = order || categories;

  useEffect(() => {
    fetchCategories();
    axios.get(`${API}/contributors/list`)
      .then((r) => setPractitioners((r.data || []).map((c) => c.user).filter(Boolean)))
      .catch(() => {});
  }, []);

  const onEdit = (category) => {
    handleEditCategory(category, categoryFormRef);
  };

  const onSave = async () => {
    await saveCategory(fetchCategories);
  };

  const onDelete = async (id) => {
    setOrder(null);
    await deleteCategory(id, fetchCategories);
  };

  const onDragStart = (e, id) => {
    dragRef.current = id;
    setDragId(id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', id); } catch { /* Safari */ }
    }
  };

  const onDragOver = (e, overId) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const heldId = dragRef.current;
    if (!heldId || heldId === overId) return;
    setOrder((current) => {
      const list = [...(current || categories)];
      const from = list.findIndex((c) => c.id === heldId);
      const to = list.findIndex((c) => c.id === overId);
      if (from === -1 || to === -1) return current;
      list.splice(to, 0, list.splice(from, 1)[0]);
      return list;
    });
  };

  const onDrop = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    dragRef.current = null;
    setDragId(null);
    if (!order) return;
    setSavingOrder(true);
    try {
      await axios.put(`${API}/forum/categories/reorder`,
        { category_ids: order.map((c) => c.id) });
      toast.success('Forum order saved');
      await fetchCategories();
      setOrder(null);
    } catch (e) {
      toast.error('Could not save the new order');
    }
    setSavingOrder(false);
  };

  return (
    <div className="space-y-6">
      <Card ref={categoryFormRef}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{editingCategory ? 'Edit Category' : 'Create Forum Category'}</span>
            {editingCategory && (
              <Button variant="ghost" size="sm" onClick={resetCategoryForm}>
                Cancel Edit
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Category Name</Label>
            <Input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              placeholder="e.g., General Discussion"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              placeholder="Category description"
              rows={3}
            />
          </div>
          <div>
            <Label>Access Tier</Label>
            <Select
              value={categoryForm.required_tier}
              onValueChange={(value) => setCategoryForm({ ...categoryForm, required_tier: value })}
            >
              <SelectTrigger data-testid="category-tier-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-no-translate>
                {TIER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`category-tier-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {categoryForm.required_tier === 'free_comp'
                ? 'Only comped practitioners and admins. Members never see this channel listed at all — it is hidden, not locked.'
                : 'Members at this tier and above can access it (Free-Comp always has full access).'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={categoryForm.allow_homework}
              onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, allow_homework: checked })}
            />
            <Label>Allow Homework Submissions</Label>
          </div>
          <div>
            <Label className="flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5 text-amber-500" /> Section Lead
            </Label>
            <Select
              value={categoryForm.lead_practitioner_id || 'none'}
              onValueChange={(value) => setCategoryForm({ ...categoryForm, lead_practitioner_id: value === 'none' ? '' : value })}
            >
              <SelectTrigger data-testid="category-lead-select">
                <SelectValue placeholder="No section lead" />
              </SelectTrigger>
              <SelectContent data-no-translate>
                <SelectItem value="none">No section lead</SelectItem>
                {practitioners.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`category-lead-${p.id}`}>
                    {p.full_name || p.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              The lead practitioner gets a "Section Lead" badge in this channel and can pin messages.
            </p>
          </div>
          <Button onClick={onSave} className="btn-primary w-full">
            {editingCategory ? 'Update Category' : 'Create Category'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing Categories ({categories.length})</CardTitle>
          <p className="text-sm text-gray-500">
            Drag a category by its handle to set the order members see in Forum Chat.
            {savingOrder ? ' Saving…' : ''}
          </p>
        </CardHeader>
        <CardContent>
          {categories.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No categories created yet</p>
          ) : (
            <div className="space-y-3">
              {ordered.map((category, index) => (
                <div
                  key={category.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, category.id)}
                  onDragOver={(e) => onDragOver(e, category.id)}
                  onDrop={onDrop}
                  onDragEnd={onDrop}
                  className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 ${
                    dragId === category.id ? 'opacity-60 border-cyan-500' : ''
                  }`}
                  data-testid={`category-row-${category.id}`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700"
                      title="Drag to reorder" data-testid={`category-drag-${category.id}`}>
                      <GripVertical className="w-5 h-5" />
                    </span>
                    <span className="text-xs text-gray-400 w-5 text-right tabular-nums"
                      data-testid={`category-position-${category.id}`}>{index + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{category.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        category.required_tier === 'free_comp'
                          ? 'bg-amber-100 text-amber-900'
                          : 'bg-cyan-100 text-cyan-800'
                      }`} data-testid={`category-tier-badge-${category.id}`}>
                        {TIER_LABEL[category.required_tier] || 'Legacy (by name)'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => onEdit(category)} data-testid={`category-edit-btn-${category.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Category?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete "{category.name}" and all posts in it.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(category.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminForumTab;
